import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";

import { useSeasonsData } from "@/hooks/products/useProducts";
import { useRecalculateProductPrice, useUpdateProductPrice } from "@/hooks/products/useProductPrice.js";
import useFilterStore from "@/stores/useFilterStore";
import { computeSeasonAveragePrice, filterPriceHistoryBySeason } from "@/utils/priceHistorySeason";

export default function useProductPriceHistoryController() {
	const [product, setProduct] = useState(null);
	const [allRows, setAllRows] = useState([]);
	const [seasons, setSeasons] = useState([]);
	const [currentSeasonIndex, setCurrentSeasonIndex] = useState(0);
	const [selectedEstateParentId, setSelectedEstateParentId] = useState("");

	const [searchParams] = useSearchParams();
	const priceAction = searchParams.get("action");
	const priceProductId = searchParams.get("product");

	const { mutate: loadAllData, isLoading: isLoadingData } = useSeasonsData();
	const { mutate: updatePrice } = useUpdateProductPrice();
	const { mutate: recalculatePrice, isLoading: isRecalculatingPrice } = useRecalculateProductPrice();

	const navigate = useNavigate();
	const location = useLocation();

	const filter = useFilterStore((state) => state.filter);
	const selectedEstateParentIds = filter?.estate_parent_id;
	const selectedCurrencyType = filter?.type_currency;

	const { user } = useSelector((state) => state.auth);
	const allEstatesParents = useMemo(() => user?.estates_parents || [], [user?.estates_parents]);

	const estatesParentsOptions = useMemo(() => {
		if (!selectedEstateParentIds || !allEstatesParents.length) return allEstatesParents;
		const selectedIds = new Set(selectedEstateParentIds.split(",").filter(Boolean).map(String));
		return allEstatesParents.filter((estateParent) => selectedIds.has(String(estateParent.value)));
	}, [selectedEstateParentIds, allEstatesParents]);

	const estateParentOptions = useMemo(() => {
		if (!estatesParentsOptions || estatesParentsOptions.length === 0) return [];
		return estatesParentsOptions.map((estateParent) => ({
			value: String(estateParent.value),
			label: estateParent.label || estateParent.name || String(estateParent.value),
		}));
	}, [estatesParentsOptions]);

	const activeEstateParentId = useMemo(() => {
		return selectedEstateParentId || selectedEstateParentIds?.split(",").find(Boolean) || estateParentOptions[0]?.value || "";
	}, [selectedEstateParentId, selectedEstateParentIds, estateParentOptions]);

	const currentSeason = seasons[currentSeasonIndex];

	const tableData = useMemo(() => {
		if (!activeEstateParentId || !currentSeason) {
			return [];
		}

		const rowsForSeason = filterPriceHistoryBySeason(allRows, currentSeason, activeEstateParentId);
		if (rowsForSeason.length > 0) {
			return rowsForSeason;
		}

		return [
			{
				id: `empty-season-${currentSeasonIndex}`,
				created_at: currentSeason.start_date,
				price: null,
				isEmptySeason: true,
				seasonLabel: currentSeason.label || `Temporada ${currentSeasonIndex + 1}`,
				seasonIndex: currentSeasonIndex,
				active: currentSeason.active,
			},
		];
	}, [activeEstateParentId, allRows, currentSeason, currentSeasonIndex]);

	const seasonAveragePrice = useMemo(() => computeSeasonAveragePrice(tableData), [tableData]);

	const seasonOptions = useMemo(() => {
		return seasons.map((season, index) => ({
			value: index,
			label: season.label || `Temporada ${index + 1}`,
		}));
	}, [seasons]);

	const handleEstateParentChange = (value) => {
		setSelectedEstateParentId(value);
		if (priceProductId && selectedCurrencyType && value) {
			loadProductData(priceProductId, selectedCurrencyType, value);
		}
	};

	const handleSeasonChange = (nextSeasonIndex) => {
		if (nextSeasonIndex !== null && nextSeasonIndex !== undefined) {
			setCurrentSeasonIndex(nextSeasonIndex);
		}
	};

	const loadProductData = useCallback(
		(productCode, typeCurrency, estateParentId, callbacks = {}) => {
			const requestParams = {
				product_code: productCode,
				type_currency: typeCurrency,
			};

			if (estateParentId) {
				requestParams.estate_parent_id = estateParentId;
			}

			loadAllData(requestParams, {
				onSuccess: (response) => {
					const fetchedSeasons = response?.seasons || [];
					const fetchedRows = response?.price_history || [];
					const fetchedProduct = response?.product || null;
					setSeasons(fetchedSeasons);
					setAllRows(fetchedRows);
					setProduct(fetchedProduct);
					const activeSeasonIndex = fetchedSeasons.findIndex((item) => item.active);
					setCurrentSeasonIndex(Math.max(activeSeasonIndex, 0));
					if (callbacks.onSuccess) callbacks.onSuccess();
				},
				onError: () => {
					toast.error("Error al cargar datos del producto");
					if (callbacks.onError) callbacks.onError();
				},
			});
		},
		[loadAllData],
	);

	const handleDocumentClick = (row) => {
		if (!row) return;
		if (row.order_sale_id && row.order_sale_code) {
			window.open(`/facturas/ver/${row.order_sale_code}`, "_blank", "noopener,noreferrer");
			return;
		}
		if (row.order_delivery_id && row.order_delivery_is_without_document && row.order_delivery_code) {
			window.open(`/sin_documento/ver/${row.order_delivery_code}`, "_blank", "noopener,noreferrer");
			return;
		}
		if (row.order_delivery_id && row.order_delivery_code) {
			window.open(`/guias_despacho/ver/${row.order_delivery_code}`, "_blank", "noopener,noreferrer");
		}
	};

	const handleClose = () => {
		const params = new URLSearchParams(location.search);
		params.delete("product");
		params.delete("action");
		const nextSearch = params.toString();
		navigate(
			{
				pathname: location.pathname,
				search: nextSearch ? `?${nextSearch}` : "",
				hash: location.hash,
			},
			{ replace: true },
		);
	};

	const handlePriceSave = async (nextPrice) => {
		const numericPrice = Number(nextPrice);
		if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
			toast.error("Ingrese un precio válido");
			return;
		}

		if (!product?.id) {
			toast.error("No se pudo identificar el producto");
			return;
		}

		const params = {
			id: product.id,
			price: numericPrice,
			currency: selectedCurrencyType,
			estate_parent_id: activeEstateParentId,
		};

		await new Promise((resolve, reject) => {
			updatePrice(params, {
				onSuccess: () => {
					toast.success("Precio guardado correctamente");
					globalThis.dispatchEvent(
						new CustomEvent("product-price-updated", {
							detail: {
								productId: product.id,
								productCode: priceProductId,
								estateParentId: activeEstateParentId,
								currency: selectedCurrencyType,
								price: numericPrice,
							},
						}),
					);
					loadProductData(priceProductId, selectedCurrencyType, activeEstateParentId);
					resolve();
				},
				onError: () => {
					toast.error("No se pudo guardar el precio");
					reject(new Error("No se pudo guardar el precio"));
				},
			});
		});
	};

	const handleRecalculateFromOrigin = useCallback(() => {
		if (!priceProductId) {
			toast.error("No se pudo identificar el producto");
			return;
		}

		if (!activeEstateParentId) {
			toast.error("Seleccione un campo antes de recalcular");
			return;
		}

		recalculatePrice(
			{
				product_code: priceProductId,
				estate_parent_id: activeEstateParentId,
			},
			{
				onSuccess: (response) => {
					if (response?.status === "error") {
						toast.error(response?.data || "No se pudo recalcular el precio");
						return;
					}

					toast.success("Precios recalculados desde el origen");
					if (product?.id) {
						globalThis.dispatchEvent(
							new CustomEvent("product-price-updated", {
								detail: {
									productId: product.id,
									productCode: priceProductId,
									estateParentId: activeEstateParentId,
									currency: selectedCurrencyType,
								},
							}),
						);
					}
					loadProductData(priceProductId, selectedCurrencyType, activeEstateParentId);
				},
				onError: (error) => {
					const message = error?.response?.data?.data || error?.response?.data?.message || "No se pudo recalcular el precio";
					toast.error(message);
				},
			},
		);
	}, [activeEstateParentId, loadProductData, priceProductId, product, recalculatePrice, selectedCurrencyType]);

	useEffect(() => {
		if (priceAction !== "price" || !priceProductId || !selectedCurrencyType) return;
		setProduct(null);
		setAllRows([]);
		setSeasons([]);
		setCurrentSeasonIndex(0);
		setSelectedEstateParentId("");
		const initialEstateParentId = selectedEstateParentIds?.split(",").find(Boolean) || "";
		loadProductData(priceProductId, selectedCurrencyType, initialEstateParentId || null);
	}, [priceAction, priceProductId, selectedCurrencyType, selectedEstateParentIds, loadProductData]);

	return {
		product,
		tableData,
		seasons,
		currentSeasonIndex,
		seasonAveragePrice,
		isLoadingAdditionalSeason: false,
		selectedEstateParentId,
		isLoadingSeasons: isLoadingData,
		isLoadingPriceHistory: isLoadingData,
		estateParentOptions,
		seasonOptions,
		handleEstateParentChange,
		handleSeasonChange,
		handleDocumentClick,
		handleClose,
		handlePriceSave,
		handleRecalculateFromOrigin,
		isRecalculatingPrice,
		activeEstateParentId,
	};
}
